import * as React from 'react';
import { classNamesFunction, FontIcon } from '@fluentui/react';
import {
    IPinActionButtonProps,
    IPinActionButtonStyleProps,
    IPinActionButtonStyles,
} from './PinActionButton.types';
import { assert } from 'textfield-with-action';
import { isInsideBoundingBox } from 'grouped-combobox-web';
import {
    AppContext,
    isPinningIcon,
    canShowIcon,
    fontToIcon,
    getFontAndPinnedState,
    iconNames,
    IFontFamily,
} from 'modern-font-picker-core';

type MouseState = 'rest' | 'hover' | 'pressed';

const getClassNames = classNamesFunction<IPinActionButtonStyleProps, IPinActionButtonStyles>();

function preventDefaultCallback(
    ev: React.MouseEvent<unknown> | React.KeyboardEvent<unknown>
): void {
    ev.preventDefault();
}

export const PinActionButtonBase: React.FunctionComponent<IPinActionButtonProps> = (
    props: IPinActionButtonProps
): React.ReactElement | null => {
    const {
        componentRef,
        item,
        isActive,
        styles,
        theme,
        tooltipText,
        mouseLeaveBoundingBox,
        refreshContainingMenu,
        onRenderTooltip,
        labelElementId,
        allowFontInfoPaneCommands = true,
    } = props;

    const { disabled } = item;

    const rootRef = React.useRef<HTMLDivElement>(null);

    // Semi hack to force refresh the component
    const [, forceRefresh] = React.useReducer((x) => x + 1, 0);

    React.useImperativeHandle(componentRef, () => ({
        refresh: forceRefresh,
        setHover: (hover: boolean): void => {
            setMouseState(hover ? 'hover' : 'rest');
            if (!hover) {
                // If we're programmatically removing hover state, reset the touch flags
                isTouchEvent.current = false;
                touchedEmptyIcon.current = false;
            }
        },
    }));

    const appContext = React.useContext(AppContext);
    const { isSubscriber, fontIconButtonClicked, iconResources } = appContext;

    const [mouseState, setMouseState] = React.useState<MouseState>('rest');
    const isTouchEvent = React.useRef<boolean>(false);

    const [font, isPinned] = getFontAndPinnedState(item);

    const newIconName = canShowIcon(font, isSubscriber)
        ? fontToIcon(
              item,
              isSubscriber || false,
              // isActive is from parent, mouseState is for this button
              isActive || mouseState !== 'rest',
              iconResources
          )
        : iconNames.empty;

    // when we render an empty icon, don't process mouse events
    const isEmptyIcon = newIconName === iconNames.empty;
    const isEmptyOrDisabled = isEmptyIcon || disabled;

    // If the icon is supposed to show by default, we need to initialize the state with it
    // so the icon doesn't fade in
    const [currentIcon, setCurrentIcon] = React.useState<string | undefined>(
        isEmptyIcon ? undefined : newIconName
    );
    const canInteract =
        allowFontInfoPaneCommands || isPinningIcon(currentIcon ?? iconNames.empty, iconResources);

    // Only update the currentIcon if the newIcon isn't empty
    // If the newIcon is empty, we'll reset currentIcon to undefined when the opacity transition
    // has finished in `onTransitionEnd`
    if (!isEmptyIcon && currentIcon !== newIconName) {
        // We want to show an icon, and the icon differs from our previous state,
        // so update our state
        setCurrentIcon(newIconName);
    }

    const onTransitionEnd = React.useCallback(
        (ev: React.TransitionEvent<HTMLElement>) => {
            if (ev.propertyName !== 'opacity') {
                return;
            }
            if (isEmptyIcon) {
                // We want to show an empty icon, and the transition to 0 opacity has completed,
                // so reset our currentIcon to undefined
                setCurrentIcon(undefined);
            }
        },
        [isEmptyIcon]
    );

    const touchedEmptyIcon = React.useRef<boolean>(false);

    const onMouseOver = React.useCallback(
        (ev: React.MouseEvent<unknown>) => {
            ev.preventDefault();
            if (!isEmptyOrDisabled) {
                setMouseState((state) => (state === 'rest' ? 'hover' : state));
            } else if (isTouchEvent.current) {
                // If the icon is empty and the user touches it, set hover state to make it visible
                setMouseState('hover');
            }
        },
        [isEmptyOrDisabled]
    );

    const onMouseOut = React.useCallback(() => setMouseState('rest'), []);

    const onMouseDown = React.useCallback(
        (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            // If the icon was visible, but when the user touched it it wasn't,
            // don't "press" the button
            if (!isEmptyOrDisabled && !touchedEmptyIcon.current) {
                setMouseState('pressed');
            }

            ev.stopPropagation();
            ev.preventDefault();
        },
        [isEmptyOrDisabled]
    );

    const onTimerCallback = React.useCallback(
        (mouseX: number, mouseY: number) => {
            if (!rootRef.current) {
                return;
            }

            const mouseInsideBoundingBox = isInsideBoundingBox(
                mouseX,
                mouseY,
                rootRef.current.getBoundingClientRect()
            );

            if (!mouseInsideBoundingBox) {
                // set the pin button to rest state
                setMouseState('rest');

                // tell the parent GroupedComboBoxButton element to reset its state too
                mouseLeaveBoundingBox?.();
            }
        },
        [mouseLeaveBoundingBox]
    );

    const onMouseUp = React.useCallback(
        (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            if (isEmptyOrDisabled || touchedEmptyIcon.current) {
                // The "touch" is ending, so reset this flag
                touchedEmptyIcon.current = false;
                isTouchEvent.current = false;
                return;
            }

            // If this was a touch event, go directly to the rest state as onMouseOut won't be called
            setMouseState((state) =>
                state === 'pressed' ? (isTouchEvent.current ? 'rest' : 'hover') : state
            );
            isTouchEvent.current = false;

            canInteract && fontIconButtonClicked(font, isPinned, ev, refreshContainingMenu);

            // SEMI-HACK: After a font is pinned or unpinned, the element can shift to a new location.
            // However the `mouseLeave` event doesn't fire in that case, which causes the element to remain
            // in hover state.
            //
            // To work around this bug, we set a timer to asynchronously check the mouse position agains the
            // new element's bounding box. If the mouse is outside the bounding box, we set the element back
            // to rest state.
            window.setTimeout(
                onTimerCallback,
                1, // 1 milisecond
                ev.pageX, // current mouse X coordinate
                ev.pageY // current mouse Y coordinate
            );

            ev.stopPropagation();
            ev.preventDefault();
        },
        [
            isEmptyOrDisabled,
            canInteract,
            fontIconButtonClicked,
            font,
            isPinned,
            refreshContainingMenu,
            onTimerCallback,
        ]
    );

    const onKeyDown = React.useCallback(
        (ev: React.KeyboardEvent<HTMLDivElement>) => {
            if (isEmptyOrDisabled || !canInteract) {
                return;
            }

            if (ev.key === 'Enter' || ev.key === ' ') {
                fontIconButtonClicked(font, isPinned, ev, refreshContainingMenu);

                // When a font is pinned or unpinned through keyboard, we don't have a convenient
                // way to refresh this component externally. Use this hack to force refresh the icon.
                forceRefresh();

                ev.stopPropagation();
                ev.preventDefault();
            }
        },
        [
            isEmptyOrDisabled,
            canInteract,
            fontIconButtonClicked,
            font,
            isPinned,
            refreshContainingMenu,
        ]
    );

    const { fontType } = font as IFontFamily;

    assert(theme);
    const styleProps: IPinActionButtonStyleProps = {
        isPremium: fontType === 'premium' && !isSubscriber,
        isHover: mouseState === 'hover',
        isPressed: mouseState === 'pressed',
        // Regardless of our currentIcon, if on this tick we want to show an empty icon,
        // then start the transition to 0% opacity
        // When this transition is finished we'll update our state to reflect the hidden icon
        // Otherwise, setting `isVisible` to true will cause our icon to fade in (or stay visible)
        isVisible: !isEmptyIcon,
        theme: theme,
        disabled: disabled,
        canInteract,
    };
    const processedStyles = getClassNames(styles, styleProps);

    const onTouchStart = React.useCallback(() => {
        isTouchEvent.current = true;
        // Track whether or not the icon was empty at the time of touch
        touchedEmptyIcon.current = isEmptyOrDisabled ?? false;
    }, [isEmptyOrDisabled]);

    const onContextMenu = React.useCallback((ev: React.MouseEvent<unknown>) => {
        // No other mouse events will happen after this, so if this was triggered by touch then reset isTouchEvent
        isTouchEvent.current = false;
        touchedEmptyIcon.current = false;
        ev.preventDefault();
        ev.stopPropagation();
    }, []);

    const content = (
        // Focus styling is taken care of with css
        // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
        <div
            ref={rootRef}
            role={canInteract ? 'button' : 'img'}
            aria-label={tooltipText}
            aria-describedby={labelElementId}
            aria-disabled={disabled}
            data-no-horizontal-wrap={true}
            className={processedStyles.root}
            onMouseOver={onMouseOver}
            onMouseOut={onMouseOut}
            onMouseDown={canInteract ? onMouseDown : preventDefaultCallback}
            onMouseUp={canInteract ? onMouseUp : preventDefaultCallback}
            onKeyDown={canInteract ? onKeyDown : undefined}
            onTouchStart={canInteract ? onTouchStart : undefined}
            onContextMenu={canInteract ? onContextMenu : undefined}
            tabIndex={-1}
        >
            <div className={processedStyles.iconWrapper} onTransitionEnd={onTransitionEnd}>
                {currentIcon !== undefined && (
                    <FontIcon
                        iconName={currentIcon}
                        onMouseOver={onMouseOver}
                        onMouseOut={onMouseOut}
                        className={processedStyles.icon}
                    />
                )}
            </div>
        </div>
    );

    return tooltipText && onRenderTooltip
        ? onRenderTooltip({
              targetElement: rootRef,
              tooltip: tooltipText,
              showTooltipOnRight: false,
              mainContent: content,
          })
        : content;
};
